
import React, { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';
import { LetterEvaluation } from '@/utils/wordUtils';

interface TileProps {
  letter?: string;
  value?: string; // Add this prop to match what's being passed from Grid
  evaluation?: LetterEvaluation;
  status?: string; // Add this prop to match what's being passed from Grid
  revealed?: boolean;
  position: number;
  animated?: boolean;
  isRevealing?: boolean; // Add this prop to match what's being passed from Grid
  isCompleted?: boolean; // Add this prop to match what's being passed from Grid
}

const Tile: React.FC<TileProps> = ({ 
  letter = '', 
  value = '', // Use value prop if passed
  evaluation, 
  status = '', // Use status prop if passed
  revealed = false, 
  isRevealing = false, // Default value
  isCompleted = false, // Default value
  position,
  animated = true
}) => {
  const [animate, setAnimate] = useState(false);
  // Use either value or letter prop (value takes precedence)
  const displayLetter = value || letter;
  const isFilled = displayLetter !== '';

  useEffect(() => {
    if (isFilled && !revealed && !isRevealing && animated) {
      setAnimate(true);
      const timer = setTimeout(() => setAnimate(false), 100);
      return () => clearTimeout(timer);
    }
  }, [displayLetter, revealed, isRevealing, isFilled, animated]);

  // Define the background colors based on evaluation or status
  const getBgColor = () => {
    if (!revealed && !isCompleted) return 'bg-transparent';
    
    const evalToUse = status || (evaluation ? evaluation : '');
    
    switch (evalToUse) {
      case 'correct':
        return 'bg-fourdle-correct text-white';
      case 'present':
        return 'bg-fourdle-present text-white';
      case 'absent':
        return 'bg-fourdle-absent text-white';
      default:
        return 'bg-transparent';
    }
  };

  return (
    <div 
      className={cn(
        'tile w-full aspect-square flex items-center justify-center',
        isFilled && 'filled',
        (revealed || isCompleted) && 'revealed',
        (evaluation || status) && (revealed || isCompleted) && (status || evaluation || ''),
        animate && 'animate-pop',
        getBgColor()
      )}
      style={{ animationDelay: animated && (revealed || isRevealing) ? `${position * 200}ms` : '0ms' }}
    >
      <div className={cn(
        'tile-inner text-xl sm:text-xl md:text-2xl w-full h-full flex items-center justify-center', 
        animated && (revealed || isRevealing) && 'animate-flip'
      )}>
        {displayLetter}
      </div>
    </div>
  );
};

export default Tile;
